import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface Deal {
  id: string;
  quantity: number;
  price: number;
  startDate: string;
  endDate: string;
}

export interface DeliveryZone {
  id: string;
  towns: string[];
  deliveryPrice: number;
}

export interface DeliveryRange {
  id: string;
  minDistance: number;
  maxDistance: number;
  deliveryPrice: number;
}

export interface PricingState {
  pricePerDozen: number;
  retailerPriceUnder50: number;
  retailerPrice50Plus: number;
  deals: Deal[];
  deliveryZones: DeliveryZone[];
  deliveryRanges: DeliveryRange[];
}

// Helper function to load price from localStorage
const loadPriceFromStorage = (key: string): number => {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return 0;
    const parsed = parseFloat(stored);
    return isNaN(parsed) ? 0 : parsed;
  } catch (error) {
    console.error(`Error loading ${key} from localStorage:`, error);
    return 0;
  }
};

// Helper function to load deals from localStorage
const loadDealsFromStorage = (): Deal[] => {
  try {
    const stored = localStorage.getItem('pricing-deals');
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('Error loading deals from localStorage:', error);
    return [];
  }
};

// Helper function to load delivery zones from localStorage
const loadDeliveryZonesFromStorage = (): DeliveryZone[] => {
  try {
    const stored = localStorage.getItem('pricing-delivery-zones');
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('Error loading delivery zones from localStorage:', error);
    return [];
  }
};

// Helper function to load delivery ranges from localStorage
const loadDeliveryRangesFromStorage = (): DeliveryRange[] => {
  try {
    const stored = localStorage.getItem('pricing-delivery-ranges');
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('Error loading delivery ranges from localStorage:', error);
    return [];
  }
};

const initialState: PricingState = {
  pricePerDozen: loadPriceFromStorage('price-per-dozen'),
  retailerPriceUnder50: loadPriceFromStorage('retailer-price-under-50'),
  retailerPrice50Plus: loadPriceFromStorage('retailer-price-50-plus'),
  deals: loadDealsFromStorage(),
  deliveryZones: loadDeliveryZonesFromStorage(),
  deliveryRanges: loadDeliveryRangesFromStorage()
};

const pricingSlice = createSlice({
  name: 'pricing',
  initialState,
  reducers: {
    setPricePerDozen: (state, action: PayloadAction<number>) => {
      state.pricePerDozen = action.payload;
    },
    setRetailerPriceUnder50: (state, action: PayloadAction<number>) => {
      state.retailerPriceUnder50 = action.payload;
    },
    setRetailerPrice50Plus: (state, action: PayloadAction<number>) => {
      state.retailerPrice50Plus = action.payload;
    },
    addDeal: (state, action: PayloadAction<Deal>) => {
      state.deals.push(action.payload);
      // Sort deals by quantity (ascending)
      state.deals.sort((a, b) => a.quantity - b.quantity);
    },
    removeDeal: (state, action: PayloadAction<string>) => {
      state.deals = state.deals.filter(deal => deal.id !== action.payload);
    },
    addDeliveryZone: (state, action: PayloadAction<DeliveryZone>) => {
      state.deliveryZones.push(action.payload);
    },
    removeDeliveryZone: (state, action: PayloadAction<string>) => {
      state.deliveryZones = state.deliveryZones.filter(zone => zone.id !== action.payload);
    },
    addDeliveryRange: (state, action: PayloadAction<DeliveryRange>) => {
      state.deliveryRanges.push(action.payload);
      // Sort ranges by minDistance (ascending)
      state.deliveryRanges.sort((a, b) => a.minDistance - b.minDistance);
    },
    removeDeliveryRange: (state, action: PayloadAction<string>) => {
      state.deliveryRanges = state.deliveryRanges.filter(range => range.id !== action.payload);
    }
  }
});

export const {
  setPricePerDozen,
  setRetailerPriceUnder50,
  setRetailerPrice50Plus,
  addDeal,
  removeDeal,
  addDeliveryZone,
  removeDeliveryZone,
  addDeliveryRange,
  removeDeliveryRange
} = pricingSlice.actions;

export default pricingSlice.reducer;
